import { Box, Stack, Switch } from '@ff-it/ui';
import { type ReactElement, useState } from 'react';
import { SectionCard } from 'components/SectionCard';
import { usePermissionContext } from './context';
import type { SectionModel, SectionType } from './types';

function Model({ title, perms }: SectionModel): ReactElement {
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const { loading, permissions: enabledPermissions, toggle } = usePermissionContext();

  return (
    <SectionCard title={title} expanded={isOpen} onToggle={() => setIsOpen((open) => !open)}>
      <Stack as="ul" space="0" m="0" p="0" style={{ listStyle: 'none' }}>
        {perms.map(({ id, name, description }, index) => {
          const enabled = Boolean(enabledPermissions[id]);
          return (
            <Box as="li" key={id} borderTop={index > 0 ? 'subtle' : undefined}>
              <Box
                as="label"
                display="flex"
                alignItems="flex-start"
                justifyContent="space-between"
                mb="0"
                px="3"
                py="2"
                style={{ cursor: loading ? 'default' : 'pointer' }}
              >
                <Box ml="2" mr="auto">
                  <Box>{name}</Box>
                  {/* biome-ignore lint/security/noDangerouslySetInnerHtml: should be safe */}
                  <Box color="gray500" fontSize="xs" dangerouslySetInnerHTML={{ __html: description }} />
                </Box>
                <Switch disabled={loading} checked={enabled} onChange={() => toggle(id, !enabled)} />
              </Box>
            </Box>
          );
        })}
      </Stack>
    </SectionCard>
  );
}

export function Section({ title, models }: SectionType): ReactElement {
  return (
    <Stack mb="3" space="3">
      <Box as="h4" m="0">
        {title}
      </Box>
      <Stack space="3">
        {models.map((model) => (
          <Model {...model} key={model.model_key} />
        ))}
      </Stack>
    </Stack>
  );
}
